import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Badge, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
  useGetSingleHOneQuery,
  useGetValueQuery,
} from "../../../app/features/apiSlice";
import Loader from "../../../components/Loader";
import Error from "../../../components/Error";
import { setPageTitle } from "../../../app/features/DataSlice";
import CurrencyValue from "../../../components/CurrencyValue";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import moment from "moment";
import { BANK_PAYMENT_METHODS } from "../../../Constants";
import HOneIngotDetailTable from "../../../components/HeliorOne/HOneIngotDetailTable";
import { formatNumberToLocal } from "../../../helpers";

const conRole = ["Advisor", "Manager", "Account"];

const HOneContractDetails = () => {
  const pageTitle = "XONE";
  const { t } = useTranslation();
  const { contractCode, lang } = useParams();
  const dispatch = useDispatch();
  const sessionId = useRef(Date.now()).current;

  const staticData = useGetValueQuery({ sessionId });
  const { data, isLoading, isError, error, isSuccess } = useGetSingleHOneQuery({
    sessionId,
    contractCode,
  });

  useEffect(() => {
    dispatch(setPageTitle(pageTitle));
  });

  const contractCurrency = data?.currency;
  const contractRate = data?.fx_rate;

  const txFee = BANK_PAYMENT_METHODS.includes(data?.payment_method)
    ? Number(staticData?.data?.txBankFee ?? 0)
    : Number(staticData?.data?.txCryptoFee ?? 0);
  const guaranteeMargin = Number(staticData?.data?.guaranteeMargin ?? 0);
  const totalFeePercent = txFee + guaranteeMargin;

  return (
    <div className="my-3">
      {isLoading && <Loader />}
      {isError && <Error error={error} />}
      {isSuccess && (
        <>
          <Card className="mb-2" bg="dark1 text-light">
            <Card.Header className="text-uppercase">
              {t("purchase")}
            </Card.Header>
            <Card.Body>
              <ul className="contract-details-list list-unstyled m-0">
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">{t("contractDate")}</span>
                  <span>
                    {data?.contract_date
                      ? moment(data?.contract_date).format("DD-MM-YYYY")
                      : ""}
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">{t("duration")}</span>
                  <span>
                    {data?.duration} {t("years")}
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">{t("goldValue")}</span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.purchase?.goldPrice}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">{t("spread")} </span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.purchase?.cost_spread}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">{t("manufacturing2")}</span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.purchase?.totalManufacturingCost}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">{t("contractNumber")}</span>
                  <span>{contractCode}</span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">{t("totalGrams")}</span>
                  <span>{data?.total_gr} GR</span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">
                    {t("custody")} {data?.custodyPercent}%
                  </span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.purchase?.cost_storage}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className='lh-sm'>{t("totalPurchase")}</span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.purchase?.total_payment}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className='lh-sm'>{t("guaranteeMargin2")}</span>
                  <span>{guaranteeMargin}%</span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className='lh-sm'>{t("transactionFee")}</span>
                  <span>{`${txFee}%`}</span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span className="lh-sm">
                    {t("totalPurchaseWith")}
                    {lang === "en" &&
                      `${totalFeePercent}%`}
                    {t("TXFee")}
                    {lang === "it" &&
                      `${totalFeePercent}%`}
                    :
                  </span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.purchase?.bank_transfer}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between ">
                  <span className="lh-sm">{t("status")}</span>
                  <span>
                    <Badge
                      bg={`${data?.status === "Active" ? "success" : "danger"}`}
                    >
                      {data?.status === "Active" ? t("active") : t("inactive")}
                    </Badge>
                  </span>
                </li>
              </ul>
            </Card.Body>
          </Card>
          <Card className="mb-2" bg="dark1 text-light">
            <Card.Header className="text-uppercase">
              {t("warranty")}
            </Card.Header>
            <Card.Body>
              <ul className="columns-2 list-unstyled m-0 warranty-details-list">
                <li className="mb-2 d-flex justify-content-between">
                  <span>{t("goldValue")}</span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.warranty?.goldPriceWithMargin}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span>{t("spread")} </span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.warranty?.costSpreadWithMargin}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span>
                    {t("custody")} {data?.custodyPercent}%
                  </span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.warranty?.storageCostWithMargin}
                    />
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span>{t("totGuarantee")}</span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.warranty?.total_payment_with_margin}
                    />
                  </span>
                </li>
              </ul>
            </Card.Body>
          </Card>
          <Card className="mb-2" bg="dark1 text-light">
            <Card.Header className="text-uppercase">
              {t("collection")}
            </Card.Header>
            <Card.Body>
              <ul className="columns-2 list-unstyled m-0 collection-details-list">
                <li className="mb-2 d-flex justify-content-between">
                  <span>{t("date")}</span>
                  <span>
                    {data?.collection_date
                      ? moment(data?.collection_date).format("DD-MM-YYYY")
                      : "-"}
                  </span>
                </li>
                <li className="mb-2 d-flex justify-content-between">
                  <span>{t("goldCost")}</span>
                  <span>
                    <CurrencyValue
                      currency={contractCurrency}
                      rate={contractRate}
                      amount={data?.collection?.goldFixingPrice}
                    />
                  </span>
                </li>
              </ul>
            </Card.Body>
          </Card>
          <HOneIngotDetailTable
            ingots={data?.plans}
            manufacturingRates={staticData?.data?.manufacturingCost}
            custodyPercent={data?.custodyPercent}
            spreadRate={
              data?.purchase?.goldPrice
                ? data.purchase.cost_spread / data.purchase.goldPrice
                : 0
            }
            guaranteeMargin={guaranteeMargin}
          />
          {data?.commission?.length > 0 && (
            <Card className="mb-2" bg="dark1 text-light">
              <Card.Header className="text-uppercase">
                {t("provisions")}
              </Card.Header>
              <Card.Body>
                <DataTable
                  value={data?.commission}
                  size={"small"}
                  showGridlines
                  stripedRows
                >
                  <Column field="role" header={t("role")} />
                  <Column
                    field="firstName"
                    header={t("collaborator")}
                    body={(rowData) =>
                      rowData?.user?.first_name
                        ? `${rowData.user.first_name + " " + rowData.user.last_name}`
                        : "-"
                    }
                  />
                  <Column
                    field="percentage"
                    header={t("percentage")}
                    body={(rowData) => `${(rowData.percent * 100).toFixed(2)}%`}
                  />
                  <Column field="amount" header={t("compensationAmount")} />
                  <Column
                    field="overFromNet"
                    header={t("overFromNet")}
                    body={(rowData) =>
                      rowData.overFromNet && conRole.includes(rowData.role)
                        ? `- ${rowData.overFromNet}`
                        : rowData.overFromNet
                    }
                  />
                  <Column field="" header={t("liquidated")} />
                  <Column />
                </DataTable>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default HOneContractDetails;